import { z } from 'zod';
import { Timestamp, Uuid } from './common.js';
import { ReportRunKind, RunState } from './vocab.js';

/**
 * **Reports** — documents the office asks for about a situation, built from what
 * the record holds (docs/notes/2026-09-22-reports-design.md).
 *
 * A report reads facts, analyses and connections and **never writes to the
 * record**: nothing here is a fact, a claim, a note or an edge. Its inputs are
 * the office's own prose plus a list of people; its output is a numbered,
 * immutable version.
 */

/* ————————————————————————— type registry ————————————————————————— */

export const REPORT_TYPE_KEYS = [
  'event_advance',
  'travel_brief',
  'crowd_summary',
  'principal_briefing',
  'meeting_list',
  'prospects_summary',
  'guests_to_know',
  'companies_in_room',
  'dossier',
] as const;
export const ReportTypeKey = z.enum(REPORT_TYPE_KEYS);
export type ReportTypeKey = z.infer<typeof ReportTypeKey>;

export interface ReportFieldDef {
  key: string;
  label: string;
  /** The one line under the label saying what to write there. */
  prompt: string;
  /** `without_event`: needed only when the report has no event to read instead. */
  required: boolean | 'without_event';
}

/** Whether a field must be filled, given whether the report names an event. */
export function fieldRequired(field: ReportFieldDef, hasEvent: boolean): boolean {
  return field.required === true || (field.required === 'without_event' && !hasEvent);
}

export interface ReportDetailDef {
  key: string;
  label: string;
}

export interface ReportSectionDef {
  key: string;
  title: string;
  /** One block per person (guest notes) rather than one for the report. */
  per_person: boolean;
}

export interface ReportTypeDef {
  key: ReportTypeKey;
  display_name: string;
  /** One sentence for the type picker. */
  description: string;
  people_label: string;
  max_people: number;
  fields: readonly ReportFieldDef[];
  /**
   * Details the inputs reading must find. A missing one stops the run before
   * the writer spends anything (design ruling 9).
   */
  required_details: readonly ReportDetailDef[];
  /**
   * The writer's default sections. Strong guidance, not a schema: a revise can
   * drop, merge, reorder, retitle or add sections (design ruling 7).
   */
  default_sections: readonly ReportSectionDef[];
  /**
   * The run collects public material before writing (an "investigate" type).
   * A revise never collects: it writes from the base version's material.
   */
  collects: boolean;
  /**
   * The report is about one protection account: it names the account, and
   * every person on it must be on that account (the account wall).
   */
  account_scoped: boolean;
  /**
   * Whether the report is about one tracked event: `required`, `optional`, or
   * `none`. An event report reads the event and its attendee list, and its
   * account must be the event's.
   */
  event: 'none' | 'optional' | 'required';
  /**
   * Starting sections by the person's status, for a type about one person
   * whose status decides what the document covers (the dossier). A status
   * absent here, or a type without it, uses `default_sections`. Still strong
   * guidance, not a schema.
   */
  sections_by_status?: Readonly<Record<string, readonly ReportSectionDef[]>>;
  /**
   * What the exported Sources list names. `record` (the default): the record's
   * own items, as the page lists them. `documents`: only the outside documents
   * behind them, for a document the firm hands to its client
   * (docs/notes/2026-09-28-shippable-dossier-spec.md §4).
   */
  export_sources?: 'record' | 'documents';
  /**
   * The run also reads the people's notes (never a confidential one), citable
   * as `note`. Types without it read facts, analyses and connections only.
   */
  reads_notes?: boolean;
  /**
   * The run also reads ties between the people and the account's people with
   * this status (security's `principal`), who are shown to the writer but are
   * not the report's people. Absent: no such ties are read.
   */
  ties_to_status?: string;
}

/** The starting sections for a report about a person with this status. */
export function sectionsFor(
  type: ReportTypeDef,
  status: string | null,
): readonly ReportSectionDef[] {
  return (status !== null && type.sections_by_status?.[status]) || type.default_sections;
}

/** The club event advance (design §6). */
export const EVENT_ADVANCE: ReportTypeDef = Object.freeze({
  key: 'event_advance',
  display_name: 'Event advance',
  description:
    'Who is coming, who already knows whom, who might be introduced, and a short note on each person.',
  people_label: 'Who is involved',
  max_people: 100,
  fields: [
    {
      key: 'event',
      label: 'The event',
      prompt:
        'What is the occasion, when and where, who is hosting, and anything staff should know.',
      required: 'without_event' as const,
    },
  ],
  required_details: [{ key: 'date', label: 'the date of the event' }],
  default_sections: [
    { key: 'at_a_glance', title: 'At a glance', per_person: false },
    { key: 'who_knows_whom', title: 'Who knows whom', per_person: false },
    { key: 'introductions', title: 'Introductions worth considering', per_person: false },
    { key: 'seating', title: 'Seating and pairing', per_person: false },
    /* Written in code from the event's list, when the advance has an event. */
    { key: 'guests_off_record', title: 'Guests not on your record', per_person: false },
    { key: 'guest_notes', title: 'Guest notes', per_person: true },
    { key: 'prospects', title: 'Prospects at this event', per_person: false },
  ],
  collects: false,
  account_scoped: false,
  /* Chosen, the event gives the date, place and hosts, and its attendees on the
     record fill the people (docs/notes/2026-09-24-event-advance-on-events-spec.md). */
  event: 'optional',
});

/** The security travel brief (docs/notes/2026-09-23-travel-brief-spec.md). */
export const TRAVEL_BRIEF: ReportTypeDef = Object.freeze({
  key: 'travel_brief',
  display_name: 'Travel brief',
  description: 'Advisories, local conditions, events and traveler exposure for a trip.',
  people_label: 'Travelers',
  max_people: 10,
  fields: [
    {
      key: 'itinerary',
      label: 'Itinerary',
      prompt: 'Destinations, dates, lodging, events and venues. A pasted itinerary works.',
      required: true,
    },
    {
      key: 'context',
      label: 'Purpose and context',
      prompt: "Purpose, whether the trip is public, known concerns, travelers' nationality.",
      required: false,
    },
  ],
  required_details: [
    { key: 'destination', label: 'a destination' },
    { key: 'dates', label: 'travel dates' },
  ],
  default_sections: [
    { key: 'summary', title: 'Summary', per_person: false },
    { key: 'key_considerations', title: 'Key considerations', per_person: false },
    { key: 'emergency', title: 'Emergency contacts', per_person: false },
    { key: 'destination', title: 'Destination', per_person: false },
    { key: 'events', title: 'Events and venues', per_person: false },
    { key: 'exposure', title: 'Traveler exposure', per_person: false },
    { key: 'access', title: 'Location and access', per_person: false },
    { key: 'law', title: 'Law and custom', per_person: false },
    { key: 'watch', title: 'Watch items', per_person: false },
    { key: 'gaps', title: 'Gaps', per_person: false },
  ],
  collects: true,
  account_scoped: true,
  event: 'none',
  export_sources: 'documents',
});

/**
 * **The crowd summary** (docs/notes/2026-09-24-crowd-summary-spec.md): the
 * readout of an event's attendee scan and the vertical's analysis frames. Its
 * people come from the event, never a picker; it collects nothing of its own.
 */
export const CROWD_SUMMARY: ReportTypeDef = Object.freeze({
  key: 'crowd_summary',
  display_name: 'Crowd summary',
  description:
    "Who on an event's attendee list shows signs of disruption, who could not be told apart, and who is clear.",
  people_label: '',
  max_people: 0,
  /* Asked before the scan, with the analysis (2026-10-03), and only ever steers
     the write-up: disruption is the same screen at every event. */
  fields: [
    {
      key: 'focus',
      label: 'Anything the write-up should cover',
      prompt: 'What the firm is most concerned about at this event, if anything.',
      required: false,
    },
  ],
  required_details: [],
  default_sections: [
    { key: 'summary', title: 'Summary', per_person: false },
    { key: 'flagged', title: 'Flagged', per_person: false },
    { key: 'unknown', title: 'To check', per_person: false },
    { key: 'no_flags', title: 'No flags', per_person: false },
    { key: 'clear', title: 'Clear', per_person: false },
    { key: 'not_assessed', title: 'Not assessed', per_person: false },
    { key: 'gaps', title: 'Gaps', per_person: false },
  ],
  collects: false,
  account_scoped: true,
  event: 'required',
});

/* ——— The per-vertical event analyses' reports (docs/notes/2026-09-24-event-analyses-per-vertical-spec.md) ——— */

/** The readout of a rated analysis: people from the event, never a picker; collects nothing. */
const RATED_READOUT = {
  people_label: '',
  max_people: 0,
  required_details: [],
  collects: false,
  event: 'required' as const,
};

/**
 * Summary first; the group the analysis raises; the open questions ("To
 * check", as the event's tiles say it); then the lists written in code. A
 * readout of a screening frame (one with an `unplaced` tier) lists its "No
 * flags" attendees apart from its clear ones.
 */
function readoutSections(
  raised: readonly ReportSectionDef[],
  clearTitle: string,
  screening = false,
): ReportSectionDef[] {
  return [
    { key: 'summary', title: 'Summary', per_person: false },
    ...raised,
    { key: 'unknown', title: 'To check', per_person: false },
    ...(screening ? [{ key: 'no_flags', title: 'No flags', per_person: false }] : []),
    { key: 'clear', title: clearTitle, per_person: false },
    { key: 'not_assessed', title: 'Not assessed', per_person: false },
    { key: 'gaps', title: 'Gaps', per_person: false },
  ];
}

/** Security's principal fit, rated against the principal's objectives. */
export const PRINCIPAL_BRIEFING: ReportTypeDef = Object.freeze({
  ...RATED_READOUT,
  key: 'principal_briefing',
  display_name: 'Principal briefing',
  description:
    "Who on an event's attendee list bears on the principal's objectives, who could not be told apart, and everyone else.",
  fields: [],
  default_sections: readoutSections(
    [{ key: 'matches', title: 'Worth meeting', per_person: false }],
    'Everyone else',
  ),
  account_scoped: true,
});

/** Who to meet, for business and personal alike, rated against what the office wants from the event. */
export const MEETING_LIST: ReportTypeDef = Object.freeze({
  ...RATED_READOUT,
  key: 'meeting_list',
  display_name: 'Meeting list',
  description:
    "Who on an event's attendee list matches what you want from it, who could not be told apart, and everyone else.",
  fields: [],
  default_sections: readoutSections(
    [{ key: 'matches', title: 'Worth meeting', per_person: false }],
    'Everyone else',
  ),
  account_scoped: false,
});

/** Wealth's prospects in the room: recent money events and diligence concerns. */
export const PROSPECTS_SUMMARY: ReportTypeDef = Object.freeze({
  ...RATED_READOUT,
  key: 'prospects_summary',
  display_name: 'Prospects at this event',
  description:
    "Who on an event's attendee list has a recent money event or a diligence concern stated in public sources, who matches what you want from the event, and everyone else.",
  fields: [],
  default_sections: readoutSections(
    [
      { key: 'money_events', title: 'Money events', per_person: false },
      { key: 'diligence', title: 'Diligence concerns', per_person: false },
      { key: 'matches', title: 'Matches your goals', per_person: false },
    ],
    'Everyone else',
    true,
  ),
  account_scoped: false,
});

/**
 * The club's attendees to know: a public profile or a stated tie to the club,
 * and what bears on the club's goals for the event. The goals section is
 * considerations, never recommendations, as everything the club reads is.
 */
export const GUESTS_TO_KNOW: ReportTypeDef = Object.freeze({
  ...RATED_READOUT,
  key: 'guests_to_know',
  display_name: 'Attendees to know',
  description:
    "Who on an event's attendee list has a public profile or a stated tie to the club, who bears on the club's goals for the event, who could not be told apart, and everyone else.",
  fields: [],
  default_sections: readoutSections(
    [
      { key: 'public', title: 'Public profile', per_person: false },
      { key: 'matches', title: "Bears on the club's goals", per_person: false },
    ],
    'Everyone else',
  ),
  account_scoped: false,
});

/**
 * A report written from the record and the event with no scan: the event's
 * attendees on the record fill the people picker, which may be left empty
 * when nobody attending is on the record.
 */
const FROM_THE_RECORD = {
  people_label: 'People on record who are attending',
  max_people: 100,
  required_details: [],
  collects: false,
  account_scoped: false,
  event: 'required' as const,
};

export const COMPANIES_IN_ROOM: ReportTypeDef = Object.freeze({
  ...FROM_THE_RECORD,
  key: 'companies_in_room',
  display_name: 'Companies in the room',
  description:
    'The attendee list grouped by company, and what is on record about the companies and people you already know.',
  /* Asked before the report is written, and only ever steers the write-up:
     nothing here is rated, so the label promises no weighting. */
  fields: [
    {
      key: 'focus',
      label: 'Anything the write-up should cover',
      prompt: 'What matters most to you at this event, if anything.',
      required: false,
    },
  ],
  default_sections: [
    { key: 'summary', title: 'Summary', per_person: false },
    { key: 'by_company', title: 'By company', per_person: false },
    { key: 'known', title: 'Companies and people you know', per_person: false },
    { key: 'gaps', title: 'Gaps', per_person: false },
  ],
});

const section = (key: string, title: string): ReportSectionDef => ({
  key,
  title,
  per_person: false,
});

/**
 * **The dossier** (docs/notes/2026-09-28-shippable-dossier-spec.md): one person,
 * written for the firm to hand to its principal as it stands. It compiles what
 * the record holds; the person's status picks the starting sections.
 */
export const DOSSIER: ReportTypeDef = Object.freeze({
  key: 'dossier',
  display_name: 'Dossier',
  description: 'One person, written from the record for the firm to hand to its principal.',
  people_label: 'Person',
  max_people: 1,
  fields: [
    {
      key: 'include',
      label: 'Anything to include',
      prompt: 'Context for this dossier, or anything the reader should hear from the firm.',
      required: false,
    },
  ],
  required_details: [],
  default_sections: [
    section('summary', 'Summary'),
    section('background', 'Background'),
    section('considerations', 'Considerations'),
    section('gaps', 'Gaps'),
  ],
  sections_by_status: {
    principal: [
      section('summary', 'Summary'),
      section('background', 'Background and career'),
      section('family', 'Family and household'),
      section('public_profile', 'Public profile and reputation'),
      section('exposure', 'Exposure'),
      section('considerations', 'Considerations'),
      section('gaps', 'Gaps'),
    ],
    subject_of_interest: [
      section('summary', 'Summary'),
      section('who', 'Who they are'),
      section('conduct', 'Record and conduct'),
      section('ties', 'Ties to the principal'),
      section('online', 'Online activity'),
      section('considerations', 'Considerations'),
      section('gaps', 'Gaps'),
    ],
    associate: [
      section('summary', 'Summary'),
      section('background', 'Background'),
      section('ties', 'Ties to the principal'),
      section('considerations', 'Considerations'),
      section('gaps', 'Gaps'),
    ],
  },
  collects: false,
  account_scoped: true,
  event: 'none',
  export_sources: 'documents',
  reads_notes: true,
  ties_to_status: 'principal',
});

export const REPORT_TYPES: Readonly<Record<ReportTypeKey, ReportTypeDef>> = Object.freeze({
  event_advance: EVENT_ADVANCE,
  travel_brief: TRAVEL_BRIEF,
  crowd_summary: CROWD_SUMMARY,
  principal_briefing: PRINCIPAL_BRIEFING,
  meeting_list: MEETING_LIST,
  prospects_summary: PROSPECTS_SUMMARY,
  guests_to_know: GUESTS_TO_KNOW,
  companies_in_room: COMPANIES_IN_ROOM,
  dossier: DOSSIER,
});

/**
 * Which report types each vertical offers. A vertical absent here offers none,
 * and its office sees no Reports page. A type may be offered by more than one
 * vertical when its words fit each (`meeting_list`).
 */
export const REPORT_TYPES_BY_VERTICAL: Readonly<Record<string, readonly ReportTypeKey[]>> =
  Object.freeze({
    club: ['event_advance', 'guests_to_know'],
    security: ['dossier', 'travel_brief', 'crowd_summary', 'principal_briefing'],
    wealth: ['prospects_summary'],
    business: ['meeting_list', 'companies_in_room'],
    personal: ['meeting_list'],
  });

export function reportTypesFor(vertical: string): ReportTypeDef[] {
  return (REPORT_TYPES_BY_VERTICAL[vertical] ?? []).map((key) => REPORT_TYPES[key]);
}

/* ————————————————————————— version content ————————————————————————— */

export const REPORT_VERSION_ORIGINS = ['generate', 'update', 'refresh', 'revise', 'edit'] as const;
export const ReportVersionOrigin = z.enum(REPORT_VERSION_ORIGINS);
export type ReportVersionOrigin = z.infer<typeof ReportVersionOrigin>;

export const REPORT_STATES = ['active', 'archived'] as const;
export const ReportState = z.enum(REPORT_STATES);
export type ReportState = z.infer<typeof ReportState>;

/**
 * What a passage stands on. A fact or analysis must be in the version's frozen
 * record read; `input` is the office's own prose, printed as supplied and never
 * presented as a finding; `material` is an excerpt a collecting run quoted from
 * a public source, and must be in the version's material.
 */
export const ReportCitation = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('fact'), lineage_id: Uuid }).strict(),
  z.object({ kind: z.literal('analysis'), analysis_id: Uuid }).strict(),
  z.object({ kind: z.literal('input'), field: z.string().min(1).max(64) }).strict(),
  z.object({ kind: z.literal('material'), material_id: Uuid }).strict(),
  /** A cited passage the attendee scan found (`event_graph_finding`). */
  z.object({ kind: z.literal('finding'), finding_id: Uuid }).strict(),
  /** One of the office's notes on a person (`prose_input`), for a type that reads notes. */
  z.object({ kind: z.literal('note'), note_id: Uuid }).strict(),
]);
export type ReportCitation = z.infer<typeof ReportCitation>;

const ShortId = z.string().min(1).max(64);

/**
 * **Whom a passage is about**, shown above it: an attendee's name and, beneath
 * it, a short line such as their seat. Set by the engine from the attendee the
 * writer named, never by the writer's text.
 */
export const ReportPassageHeading = z.object({
  name: z.string().min(1).max(200),
  detail: z.string().max(200).nullable(),
});
export type ReportPassageHeading = z.infer<typeof ReportPassageHeading>;

export const ReportPassage = z.object({
  id: ShortId,
  /** Null on a passage about no one attendee, and on versions stored before headings. */
  heading: ReportPassageHeading.nullable().default(null),
  text: z.string().max(8000),
  citations: z.array(ReportCitation),
  /** The office changed this passage's text by hand. */
  edited: z.boolean(),
  /**
   * Cites a fact the catalog marks sensitive. Set by the server from the
   * citations, never by the writer; shown as the brief's quiet tag in the app
   * and never exported.
   */
  sensitive: z.boolean(),
});
export type ReportPassage = z.infer<typeof ReportPassage>;

export const ReportSection = z.object({
  id: ShortId,
  /** The type's default section key, or null for a section a revise added. */
  key: z.string().max(64).nullable(),
  title: z.string().min(1).max(200),
  /** The office changed the title; like an edited passage, it is kept on rewrite. */
  title_edited: z.boolean(),
  passages: z.array(ReportPassage),
});
export type ReportSection = z.infer<typeof ReportSection>;

export const ReportDetailValue = z.object({
  key: z.string().min(1).max(64),
  label: z.string().min(1).max(120),
  value: z.string().max(2000),
});
export type ReportDetailValue = z.infer<typeof ReportDetailValue>;

/**
 * **For the person preparing the report, never exported** (design §2.1). Plain
 * sentences throughout: no ids, kinds or tokens reach the page.
 */
export const ReportPanel = z.object({
  /** What is new, removed or moved since the previous version, and why. */
  changes: z.array(z.string()),
  /** Places the prose could be read two ways, and the reading used. */
  ambiguities: z.array(z.string()),
  /** Record material the report could not use, and instructions it declined. */
  cannot_use: z.array(z.string()),
  /** Selected people whose record has no completed research. */
  thin_records: z.array(z.object({ entity_id: Uuid, quick_search_only: z.boolean() })),
  /** People the prose names who match no one selected. */
  not_on_record: z.array(z.string()),
  /** Titles of sections the office edited and this version kept as edited. */
  edited_kept: z.array(z.string()),
  /** People left out because their record was deleted (design ruling 8). */
  dropped_deleted: z.number().int().nonnegative(),
  /**
   * An event report's attendees the office may still act on: identity answers
   * waiting, attendees not assessed, and attendees a check raised who are not
   * on the record (who may be added and researched; nothing is automatic). Null for a
   * report that takes no event.
   */
  attendees: z
    .object({
      to_settle: z.number().int().nonnegative(),
      not_assessed: z.number().int().nonnegative(),
      flagged_off_record: z.array(
        z.object({
          node_id: Uuid,
          name: z.string(),
          /** "<check>: <tier>" that raised them; null on versions written before it was kept. */
          why: z.string().nullable().default(null),
        }),
      ),
    })
    .nullable()
    .default(null),
});
export type ReportPanel = z.infer<typeof ReportPanel>;

export const EMPTY_REPORT_PANEL: ReportPanel = Object.freeze({
  changes: [],
  ambiguities: [],
  cannot_use: [],
  thin_records: [],
  not_on_record: [],
  edited_kept: [],
  dropped_deleted: 0,
  attendees: null,
}) as ReportPanel;

export const ReportPreferences = z.object({
  /** Section ids left out of the export. */
  sections_off: z.array(ShortId).default([]),
  /** The Sources list left out of the export. */
  sources_off: z.boolean().default(false),
  /** Revise instructions the office asked to apply to every later version. */
  kept_instructions: z.array(z.string().min(1).max(2000)).default([]),
});
export type ReportPreferences = z.infer<typeof ReportPreferences>;

/* ————————————————————————— reads ————————————————————————— */

export const ReportPerson = z.object({
  entity_id: Uuid,
  /** Null once the record is deleted; the page says "Deleted person". */
  display_name: z.string().nullable(),
  deleted: z.boolean(),
  status: z.string().nullable(),
});
export type ReportPerson = z.infer<typeof ReportPerson>;

/** Readable handles for what a version cites, so the page can list its sources. */
export const ReportCited = z.object({
  /**
   * What the brief's own fact line needs (`dashboard/src/lib/fact-copy.ts`), so
   * a cited fact reads in the report exactly as it reads on the record.
   */
  facts: z.array(
    z.object({
      lineage_id: Uuid,
      fact_type: z.string(),
      fields: z.record(z.string(), z.unknown()),
      subject_entity_id: Uuid,
      counterparty_entity_id: Uuid.nullable(),
      /** Null once deleted. */
      subject_display_name: z.string().nullable(),
      counterparty_display_name: z.string().nullable(),
      sensitive: z.boolean(),
    }),
  ),
  /** `kind` is the vertical's word; the page labels it as the brief does. */
  analyses: z.array(
    z.object({ analysis_id: Uuid, entity_name: z.string().nullable(), kind: z.string() }),
  ),
  /** Collected excerpts, with where they came from. */
  materials: z
    .array(
      z.object({
        material_id: Uuid,
        quote: z.string(),
        point: z.string(),
        url: z.string().nullable(),
        issuer: z.string().nullable(),
        published_at: z.string().nullable(),
        retrieved_at: Timestamp.nullable(),
        place: z.string().nullable(),
        /** Set when the excerpt names a traveler: `name_only` means unconfirmed. */
        binding: z.enum(['confirmed', 'name_only']).nullable(),
        person_name: z.string().nullable(),
      }),
    )
    .default([]),
  /**
   * Passages an event report cites from the attendee scan, as the version read
   * them: the attendee, the reading of their name, and whether a cited source
   * states an identifier the list supplied (`name_only` means unconfirmed).
   */
  findings: z
    .array(
      z.object({
        finding_id: Uuid,
        quote: z.string(),
        point: z.string(),
        url: z.string().nullable(),
        attendee: z.string(),
        reading: z.string(),
        /** `on_record`: quoted from the firm's own record of the attendee, not a public page. */
        binding: z.enum(['confirmed', 'name_only', 'on_record']),
      }),
    )
    .default([]),
  /** The office's own notes a version cites, as they read now. */
  notes: z
    .array(
      z.object({
        note_id: Uuid,
        text: z.string(),
        /** The note's own date when it states one, else when it was written, `YYYY-MM-DD`. */
        date: z.string(),
        person_name: z.string().nullable(),
      }),
    )
    .default([]),
});
export type ReportCited = z.infer<typeof ReportCited>;

export const ReportVersion = z.object({
  id: Uuid,
  number: z.number().int().positive(),
  origin: ReportVersionOrigin,
  created_at: Timestamp,
  inputs: z.record(z.string(), z.string()),
  people: z.array(Uuid),
  details: z.array(ReportDetailValue),
  /** The occasion's date as read from the prose, `YYYY-MM-DD`, when stated. */
  for_date: z.string().nullable(),
  sections: z.array(ReportSection),
  panel: ReportPanel,
  cited: ReportCited,
});
export type ReportVersion = z.infer<typeof ReportVersion>;

export const ReportVersionListItem = z.object({
  id: Uuid,
  number: z.number().int().positive(),
  origin: ReportVersionOrigin,
  created_at: Timestamp,
});
export type ReportVersionListItem = z.infer<typeof ReportVersionListItem>;

export const ReportRunSummary = z.object({
  id: Uuid,
  kind: ReportRunKind,
  state: RunState,
});
export type ReportRunSummary = z.infer<typeof ReportRunSummary>;

export const ReportAccountRef = z.object({ id: Uuid, label: z.string() });
export type ReportAccountRef = z.infer<typeof ReportAccountRef>;

export const ReportDetail = z.object({
  id: Uuid,
  type: ReportTypeKey,
  title: z.string(),
  state: ReportState,
  account: ReportAccountRef.nullable(),
  /** The event an event report is about, or null. */
  event: z.object({ entity_id: Uuid, name: z.string() }).nullable(),
  inputs: z.record(z.string(), z.string()),
  preferences: ReportPreferences,
  people: z.array(ReportPerson),
  created_at: Timestamp,
  current_version: ReportVersion.nullable(),
  versions: z.array(ReportVersionListItem),
  active_run: ReportRunSummary.nullable(),
  /** The last run that failed after the current version, if any. */
  last_failure: z
    .object({ code: z.string().nullable(), message: z.string(), at: Timestamp })
    .nullable(),
  /**
   * A person on the current version has had research complete, or has been
   * deleted, since the version was written. The page offers Refresh.
   */
  record_changed: z.boolean(),
});
export type ReportDetail = z.infer<typeof ReportDetail>;

export const ReportListItem = z.object({
  id: Uuid,
  type: ReportTypeKey,
  title: z.string(),
  state: ReportState,
  account: ReportAccountRef.nullable(),
  /** The event an event report is about, or null. */
  event: z.object({ entity_id: Uuid, name: z.string() }).nullable(),
  people: z.array(ReportPerson),
  for_date: z.string().nullable(),
  /** preparing: a run is live; ready: a version exists; failed: the only run so far failed. */
  status: z.enum(['preparing', 'ready', 'failed']),
  last_version_at: Timestamp.nullable(),
  created_at: Timestamp,
});
export type ReportListItem = z.infer<typeof ReportListItem>;

export const ReportList = z.object({ items: z.array(ReportListItem) });
export type ReportList = z.infer<typeof ReportList>;

export const ReportListQuery = z
  .object({
    type: ReportTypeKey.optional(),
    state: ReportState.optional(),
  })
  .strict();
export type ReportListQuery = z.infer<typeof ReportListQuery>;

/**
 * `GET /reports/:id/export` — the version as a PDF: the current one when no
 * `version` is given. The export honors the report's `sections_off` and
 * `sources_off`, and never includes the preparation panel.
 */
export const ReportExportQuery = z
  .object({
    version: z.coerce.number().int().positive().optional(),
  })
  .strict();
export type ReportExportQuery = z.infer<typeof ReportExportQuery>;

/* ————————————————————————— requests ————————————————————————— */

const Inputs = z.record(z.string().min(1).max(64), z.string().max(20_000));
/** Empty only for a type that takes no people (`max_people: 0`); the server enforces per type. */
const People = z.array(Uuid).max(500);

/** `POST /reports` — creates the report and starts its first version. */
/**
 * The price the button showed, in ledger units, on a run that collects for a
 * priced type (the travel brief) in an account enrolled in live charging. A
 * higher price is refused (`CREDIT_PRICE_CHANGED`). Ignored everywhere else.
 */
const ExpectedCreditUnits = z.number().int().min(0).optional();

export const CreateReportBody = z
  .object({
    type: ReportTypeKey,
    title: z.string().trim().min(1).max(200).optional(),
    /** Required for an account-scoped type, refused otherwise. */
    account_id: Uuid.optional(),
    /** The event, for a type that takes one; fixed for the report's life. */
    event_id: Uuid.nullable().default(null),
    people: People,
    inputs: Inputs,
    expected_credit_units: ExpectedCreditUnits,
  })
  .strict();
export type CreateReportBody = z.infer<typeof CreateReportBody>;

/**
 * `PATCH /reports/:id/title` — the office's own title. Writes no version and
 * starts no run; once set, nothing but the office changes it.
 */
export const RenameReportBody = z
  .object({
    title: z.string().trim().min(1).max(200),
  })
  .strict();
export type RenameReportBody = z.infer<typeof RenameReportBody>;

/** `POST /reports/:id/inputs` — Change inputs. */
export const UpdateReportInputsBody = z
  .object({
    people: People,
    inputs: Inputs,
    expected_credit_units: ExpectedCreditUnits,
  })
  .strict();
export type UpdateReportInputsBody = z.infer<typeof UpdateReportInputsBody>;

/** `POST /reports/:id/revise` — rewrite from the same material. */
export const ReviseReportBody = z
  .object({
    instruction: z.string().trim().min(1).max(2000),
    /** One section, or the whole report when absent. */
    section_id: ShortId.nullable().optional(),
    /** Apply this instruction to every later version as well. */
    keep_instruction: z.boolean().default(false),
    /** Edited sections the office allows this revise to rewrite. */
    rewrite_edited: z.array(ShortId).default([]),
  })
  .strict();
export type ReviseReportBody = z.infer<typeof ReviseReportBody>;

/** `POST /reports/:id/refresh`. */
export const RefreshReportBody = z
  .object({
    rewrite_edited: z.array(ShortId).default([]),
    expected_credit_units: ExpectedCreditUnits,
  })
  .strict();
export type RefreshReportBody = z.infer<typeof RefreshReportBody>;

/**
 * `POST /reports/:id/edits` — the office's own text. Each listed section is
 * replaced by the passages given; a passage id the base does not have is a new
 * passage with no sources.
 */
export const EditReportBody = z
  .object({
    base_version_id: Uuid,
    sections: z
      .array(
        z
          .object({
            id: ShortId,
            title: z.string().trim().min(1).max(200).optional(),
            passages: z.array(z.object({ id: ShortId, text: z.string().max(8000) }).strict()),
          })
          .strict(),
      )
      .min(1),
  })
  .strict();
export type EditReportBody = z.infer<typeof EditReportBody>;

/** `PATCH /reports/:id/preferences`. */
export const ReportPreferencesBody = z
  .object({
    sections_off: z.array(ShortId).optional(),
    sources_off: z.boolean().optional(),
    kept_instructions: z.array(z.string().min(1).max(2000)).optional(),
  })
  .strict();
export type ReportPreferencesBody = z.infer<typeof ReportPreferencesBody>;

/** The body every report write that starts a run answers with. */
export const ReportRunStarted = z.object({
  report_id: Uuid,
  run_id: Uuid,
});
export type ReportRunStarted = z.infer<typeof ReportRunStarted>;
